import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import AnswerStyleEditor from '@/components/answerStyle/AnswerStyleEditor';
import type { AnswerStyle } from '@/components/chat/AnswerStyle';
import useAnalytics from '@/hooks/useAnalytics';
import { useAnswerStyle } from '@/hooks/useAnswerStyle';
import useAnswerStyleActions from '@/hooks/useAnswerStyleActions';
import { semanticColors } from '@/theme/tokens';

import { GRADIENT_MARK_SX, PRIMARY_PILL_SX, QUIET_BUTTON_SX } from './answerStyle.styles';

interface AnswerStyleSignupStepProps {
  /** Id for the heading, so the sign-up modal keeps its accessible name. */
  titleId: string;
  /** Finish sign-up — called after a successful save, or on "Not now". */
  onDone: () => void;
}

/**
 * The optional last step of sign-up, shown only after the account exists: the
 * two answer-style choices with the defaults preselected. Either button
 * finishes sign-up, so the required steps and the account itself are
 * untouched. A failed save keeps the step open with the error, and "Not now"
 * still finishes. Mirrors the mobile `AnswerStyleSignupStep`.
 */
export default function AnswerStyleSignupStep({ titleId, onDone }: AnswerStyleSignupStepProps) {
  const { t } = useTranslation('common');
  const { track } = useAnalytics();
  const snapshot = useAnswerStyle();
  const { save, dismissInvite } = useAnswerStyleActions();
  const [draft, setDraft] = useState<AnswerStyle>(snapshot.style);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    track('Answer Style Editor Opened', { source: 'signup' });
    // The form (and its focused submit button) was just replaced by this step;
    // move focus to the new heading so it is announced and focus is not lost.
    headingRef.current?.focus({ preventScroll: true });
  }, [track]);

  const handleUse = async () => {
    if (saving) return;
    setSaving(true);
    setFailed(false);
    try {
      await save(draft, 'signup');
      onDone();
    } catch {
      setSaving(false);
      setFailed(true);
    }
  };

  const handleSkip = () => {
    if (saving) return;
    dismissInvite('signup');
    onDone();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', py: '4px' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <Box sx={GRADIENT_MARK_SX} aria-hidden="true">
          <SparklesIcon style={{ width: 16, height: 16 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id={titleId}
            ref={headingRef}
            tabIndex={-1}
            component="h2"
            sx={{
              outline: 'none',
              fontSize: 17,
              fontWeight: 700,
              lineHeight: 1.3,
              color: semanticColors.textPrimary,
            }}
          >
            {t('answerStyle.title')}
          </Typography>
          <Typography
            sx={{ mt: '2px', fontSize: 13, lineHeight: 1.4, color: semanticColors.textCaption }}
          >
            {t('answerStyle.intro')}
          </Typography>
        </Box>
      </Box>

      <AnswerStyleEditor value={draft} onChange={setDraft} />

      <Box
        sx={{
          pt: '12px',
          borderTop: `1px solid ${semanticColors.borderLight}`,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{ flex: '1 1 180px', minWidth: 0 }}
        >
          <Typography
            sx={{
              fontSize: 12,
              lineHeight: 1.4,
              color: failed ? semanticColors.dangerHover : semanticColors.textCaption,
            }}
          >
            {failed ? t('answerStyle.error') : t('answerStyle.note.accountBefore')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '4px', marginInlineStart: 'auto' }}>
          <Button onClick={handleSkip} aria-disabled={saving} sx={QUIET_BUTTON_SX}>
            {t('answerStyle.actions.notNow')}
          </Button>
          <Button
            onClick={() => void handleUse()}
            aria-disabled={saving}
            variant="contained"
            disableElevation
            startIcon={
              saving ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : undefined
            }
            sx={PRIMARY_PILL_SX}
          >
            {saving ? t('answerStyle.actions.saving') : t('answerStyle.actions.use')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
